import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Filter, X, Search, UserCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { submittalStatusVariant } from '@/lib/status-variants'

// Mirrors the backend `enum SubmittalStatus` (src/graphql/submittals/submittal.ts).
export const ALL_SUBMITTAL_STATUSES = [
  'DRAFT',
  'OPEN',
  'UNDER_REVIEW',
  'RETURNED_FOR_REVISION',
  'APPROVED',
  'APPROVED_AS_NOTED',
  'REJECTED',
  'CLOSED',
  'VOID',
] as const

export interface SubmittalsFilters {
  status: string[]
  specSectionId: string | null
  categoryId: string | null
  disciplineId: string | null
  packageId: string | null
  companyName: string | null
  responsibleId: string | null
  reviewerId: string | null
  ballInCourtUserId: string | null
  search: string
}

export const EMPTY_SUBMITTALS_FILTERS: SubmittalsFilters = {
  status: [],
  specSectionId: null,
  categoryId: null,
  disciplineId: null,
  packageId: null,
  companyName: null,
  responsibleId: null,
  reviewerId: null,
  ballInCourtUserId: null,
  search: '',
}

interface LookupValue {
  id: string
  kind: string
  value: string
  sortOrder: number
  archived: boolean
}

interface SubmittalsFilterBarProps {
  filters: SubmittalsFilters
  setFilters: (filters: SubmittalsFilters) => void
  lookups: LookupValue[]
  companySuggestions: string[]
  members: { id: string; name: string }[]
}

const SELECT_ALL = '__all__'

/** The single-value fields the filter dialog manages — everything except the
 *  inline ball-in-court select, the status array and the search box. */
const DIALOG_FIELDS = [
  'companyName',
  'specSectionId',
  'categoryId',
  'disciplineId',
  'packageId',
  'responsibleId',
  'reviewerId',
] as const

type DialogField = (typeof DIALOG_FIELDS)[number]

/**
 * Same layout contract as SnagsFilterBar: at most a couple of contextual
 * selects inline (here just ball-in-court — the "what waits on whom" filter),
 * ONE Filter button with an active-count bubble, everything enumerable inside
 * the dialog, active values echoed as removable badges, search pinned right.
 */
const SubmittalsFilterBar: React.FC<SubmittalsFilterBarProps> = ({
  filters,
  setFilters,
  lookups,
  companySuggestions,
  members,
}) => {
  const { t } = useTranslation()
  const [filterDialogOpen, setFilterDialogOpen] = useState(false)

  const byKind = (kind: string) => lookups.filter((l) => l.kind === kind)
  const lookupValueOf = (id: string | null) => lookups.find((l) => l.id === id)?.value ?? id ?? ''
  const memberNameOf = (id: string | null) => members.find((m) => m.id === id)?.name ?? id ?? ''

  const setField = (key: keyof SubmittalsFilters) => (value: string) =>
    setFilters({ ...filters, [key]: value === SELECT_ALL ? null : value })

  const clearField = (key: DialogField) => setFilters({ ...filters, [key]: null })

  const toggleStatus = (status: string) => {
    const next = filters.status.includes(status)
      ? filters.status.filter((s) => s !== status)
      : [...filters.status, status]
    setFilters({ ...filters, status: next })
  }

  // Mirrors SnagsFilterBar's clear: everything the dialog manages plus search;
  // the inline ball-in-court select survives, like building/floor do in snags.
  const clearAllFilters = () =>
    setFilters({
      ...filters,
      status: [],
      companyName: null,
      specSectionId: null,
      categoryId: null,
      disciplineId: null,
      packageId: null,
      responsibleId: null,
      reviewerId: null,
      search: '',
    })

  // Label + display value for the active-filter badges and the dialog rows.
  const fieldMeta: Record<DialogField, { label: string; display: (id: string | null) => string }> = {
    companyName: { label: t('Submittals_field_company'), display: (v) => v ?? '' },
    specSectionId: { label: t('Submittals_field_spec_section'), display: lookupValueOf },
    categoryId: { label: t('Submittals_field_category'), display: lookupValueOf },
    disciplineId: { label: t('Submittals_field_discipline'), display: lookupValueOf },
    packageId: { label: t('Submittals_field_package'), display: lookupValueOf },
    responsibleId: { label: t('Submittals_field_responsible'), display: memberNameOf },
    reviewerId: { label: t('Submittals_filter_reviewer'), display: memberNameOf },
  }

  const activeDialogFields = DIALOG_FIELDS.filter((key) => !!filters[key])
  const activeFilterCount = filters.status.length + activeDialogFields.length
  const hasActiveFilters = activeFilterCount > 0

  const dialogSelect = (
    key: DialogField,
    allLabel: string,
    options: { value: string; label: string }[],
  ) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{fieldMeta[key].label}</Label>
      <Select value={filters[key] || SELECT_ALL} onValueChange={setField(key)}>
        <SelectTrigger className="h-8 w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SELECT_ALL}>{allLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  const lookupOptions = (kind: string) => byKind(kind).map((l) => ({ value: l.id, label: l.value }))
  const memberOptions = members.map((m) => ({ value: m.id, label: m.name }))

  return (
    <div className="flex items-center flex-wrap gap-2">
      {/* Ball in court — the everyday "what waits on whom" filter stays inline */}
      <Select value={filters.ballInCourtUserId || SELECT_ALL} onValueChange={setField('ballInCourtUserId')}>
        <SelectTrigger className="h-8 w-auto min-w-[150px] gap-2 text-sm">
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SELECT_ALL}>{t('Submittals_all_ball_in_court')}</SelectItem>
          {members.map((member) => (
            <SelectItem key={member.id} value={member.id}>
              {member.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Separator orientation="vertical" className="h-5" />

      {/* Filter Button */}
      <Button
        variant="outline"
        size="sm"
        className="h-8 relative"
        onClick={() => setFilterDialogOpen(true)}
      >
        <Filter className="h-4 w-4" />
        {activeFilterCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground px-1">
            {activeFilterCount}
          </span>
        )}
      </Button>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {t('Submittals_filter_title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Status Section */}
            <div>
              <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                {t('Submittals_filter_status')}
              </span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {ALL_SUBMITTAL_STATUSES.map((status) => {
                  const isActive = filters.status.includes(status)
                  return (
                    <Badge
                      key={status}
                      variant={isActive ? submittalStatusVariant(status) : 'secondary'}
                      className="cursor-pointer transition-colors"
                      onClick={() => toggleStatus(status)}
                    >
                      {t(`Submittal_status_${status}`)}
                    </Badge>
                  )
                })}
              </div>
            </div>

            {/* Field Selects */}
            <div className="grid grid-cols-2 gap-3">
              {dialogSelect(
                'companyName',
                t('Submittals_all_companies'),
                companySuggestions.map((c) => ({ value: c, label: c })),
              )}
              {dialogSelect('specSectionId', t('Submittals_all_spec_sections'), lookupOptions('SPEC_SECTION'))}
              {dialogSelect('categoryId', t('Submittals_all_categories'), lookupOptions('CATEGORY'))}
              {dialogSelect('disciplineId', t('Submittals_all_disciplines'), lookupOptions('DISCIPLINE'))}
              {dialogSelect('packageId', t('Submittals_all_packages'), lookupOptions('PACKAGE'))}
              {dialogSelect('responsibleId', t('Submittals_all_responsible'), memberOptions)}
              {dialogSelect('reviewerId', t('Submittals_all_reviewers'), memberOptions)}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t">
              {hasActiveFilters ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={clearAllFilters}
                >
                  <X className="h-3 w-3" />
                  {t('Submittals_clear_filters')}
                </Button>
              ) : (
                <div />
              )}
              <Button size="sm" onClick={() => setFilterDialogOpen(false)}>
                {t('Submittals_filter_apply')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active status badges (removable inline) */}
      {filters.status.map((status) => (
        <Badge
          key={`active-${status}`}
          variant={submittalStatusVariant(status)}
          className="cursor-pointer gap-1"
          onClick={() => toggleStatus(status)}
        >
          {t(`Submittal_status_${status}`)}
          <X className="h-3 w-3" />
        </Badge>
      ))}

      {/* Active field badges (removable inline) */}
      {activeDialogFields.map((key) => (
        <Badge
          key={`active-${key}`}
          variant="info"
          className="cursor-pointer gap-1"
          onClick={() => clearField(key)}
        >
          {fieldMeta[key].label}: {fieldMeta[key].display(filters[key])}
          <X className="h-3 w-3" />
        </Badge>
      ))}

      {/* Spacer */}
      <div className="flex-grow" />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('Submittals_search_placeholder')}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="h-8 w-[180px] pl-8 text-sm"
        />
        {filters.search && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => setFilters({ ...filters, search: '' })}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  )
}

export default SubmittalsFilterBar
